import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { setHours, setMinutes, setSeconds, format, subDays } from 'date-fns';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { includes, isArray, isEmpty } from 'lodash';
import axios from 'axios';

import { Table } from '~/components';
import {
  DateTime,
  Autocomplete,
  Input,
  RadioGroup,
  Hidden,
} from '~/components/Form';
import { GFO, GFU } from '~/constants/perfil';
import { t, parseDateString } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function Reldadosabastecimentoveiculo() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [nfs, setNfs] = useState([]);
  const [items, setItems] = useState([]);
  const [temp, setTemp] = useState({});

  const formRef = useRef(null);

  const {
    glousuariotipo,
    glousuariologin,
    glousuarioorgacodigo,
    glousuarioorganome,
  } = useSelector((state) => state.user.profile);
  const { token } = useSelector((state) => state.auth);

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const validation = useMemo(
    () => ({
      datahorainicial: Yup.date()
        .typeError(t('s.geral.validacao.campoobrigatorio'))
        .transform((_, value) => parseDateString(value))
        .required(t('s.geral.validacao.campoobrigatorio')),
      datahorafinal: Yup.date()
        .typeError(t('s.geral.validacao.campoobrigatorio'))
        .transform((_, value) => parseDateString(value))
        .required(t('s.geral.validacao.campoobrigatorio'))
        .max(datahorafinal, `A data não pode ser superior a ${datahorafinal}`)
        .min(
          Yup.ref('datahorainicial'),
          'A data não pode ser inferior à data inicial'
        ),
    }),
    [datahorafinal]
  );

  const header = useMemo(
    () => [
      { id: 'descNF', label: 'N° Nota' },
      { id: 'descDtEmissaoNF', label: 'Data' },
      { id: 'descVolumeNFStr', label: 'Volume' },
    ],
    []
  );

  const initials = useMemo(
    () => ({
      datahorainicial,
      datahorafinal,
      formato: 'pdf',
    }),
    [datahorainicial, datahorafinal]
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        setLoading(true);

        formRef.current.setErrors({});

        const schema = Yup.object().shape(validation);

        await schema.validate(data, {
          abortEarly: false,
        });

        if (isEmpty(items)) throw 'Selecione uma nota fiscal antes de enviar.';

        const { datahorainicial, datahorafinal, tipo } = data;
        const { REACT_APP_URL_DOWNLOAD } = process.env;

        const formData = {
          datahorainicial,
          datahorafinal,
          tipo,
          usuario: glousuariologin,
          cod_orgao: items[0].orgaCodigo,
          des_orgao: items[0].orgaCodigo + ' - ' + items[0].orgaNome,
          des_cnpj: items[0].orgaCgc,
          cod_posto: items[0].pocoCodigo,
          des_posto: items[0].pocoDescAbrev,
          des_notafiscalnumero: items[0].descNF,
          des_notafiscalserie: items[0].descSerieNF,
          datahorarecebimentofrentista: items[0].descDataInicio,
          datahoraconfirmacaogestorposto: items[0].descDataLiberacao,
          datahoraconfirmacaogestorfrotaorgao: items[0].descDataLiberacaoOrgao,
          cpffrentista: items[0].usuaCpf,
          cpfgestorposto: items[0].usuaCpfLiberacao,
          cpfgestorfrotaorgao: items[0].usuaCpfLiberacaoOrgao,
          nomefrentista: items[0].usuaNome,
          nomegestorposto: items[0].usuaNomeLiberacao,
          nomegestorfrotaorgao: items[0].usuaNomeLiberacaoOrgao,
          des_notafiscaldata: items[0].descDtEmissaoNF,
        };

        const url = new URL(REACT_APP_URL_DOWNLOAD);

        url.search = new URLSearchParams({
          relatorio: 'AQUISICAOCOMBUSTIVELDADOSENTRADANF',
          token,
          ...formData,
        });

        const response = await axios({
          url: url.href,
          method: 'GET',
          responseType: 'blob',
        });

        const file = new Blob([response.data], { type: response.data.type });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(file);
        } else {
          const objectUrl = URL.createObjectURL(file);
          window.open(objectUrl);
        }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message;
          });

          formRef.current.setErrors(validationErrors);
        } else {
          if (isArray(err)) err.map((e) => toast.error(t(e)));
          else toast.error(t(err));
        }
      } finally {
        setLoading(false);
      }
    },
    [items, datahorafinal]
  );

  const handleGetNF = useCallback(async () => {
    try {
      setLoading(true);
      setItems([]);

      formRef.current.setErrors({});

      const formData = formRef.current.getData();

      const schema = Yup.object().shape(validation);

      await schema.validate(formData, {
        abortEarly: false,
      });

      const { data } = await api.get('descarga/retornadescargasgfo', {
        params: {
          dataHoraInicio: formData.datahorainicial,
          dataHoraFinal: formData.datahorafinal,
          codigoOrgao: formData.orga_codigo ? formData.orga_codigo : '',
          codigoPosto: formData.poco_codigo ? formData.poco_codigo : '',
        },
      });

      setNfs(data);

      if (isEmpty(data)) {
        throw t('Não existe Notas Fiscais para o período informado!');
      }
    } catch (err) {
      setNfs([]);

      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);
      } else {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    } finally {
      setLoading(false);
    }
  }, [validation, datahorafinal]);

  const handleClearSupplies = useCallback(() => {
    setNfs([]);
    setItems([]);
  }, []);

  useEffect(() => {
    handleClearSupplies();
  }, [temp]);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
      open={open}
      onClise={() => setOpen(false)}
      maxWidth="sm"
    >
      <DialogTitle>Aquisição de Combustível – Dados da Entrada NF</DialogTitle>
      <DialogContent>
        <S.Wrapper ref={formRef} onSubmit={handleSubmit} initialData={initials}>
          <DateTime
            time
            disableFuture
            label="Data Inicial"
            name="datahorainicial"
            onAccept={handleClearSupplies}
            getValue={(value) =>
              setTemp((state) => ({ ...state, start: value }))
            }
          />

          <DateTime
            time
            disableFuture
            label="Data Final"
            name="datahorafinal"
            maxDateMessage=""
            onAccept={handleClearSupplies}
            getValue={(value) => setTemp((state) => ({ ...state, end: value }))}
          />

          {(glousuariotipo === GFO || glousuariotipo === GFU) && (
            <>
              <Input
                name="orga_nome"
                value={glousuarioorganome}
                label="Órgão"
                disabled
              />

              <Hidden
                name="orga_codigo"
                value={glousuarioorgacodigo}
                label="Órgão"
                disabled
              />
            </>
          )}

          {glousuariotipo !== GFO && glousuariotipo !== GFU && (
            <Autocomplete
              name="orga_codigo"
              label="orga_nome"
              title="Órgão"
              url="orgaos"
              unique="orga_codigo"
              disabled={includes([GFO, GFU], glousuariotipo)}
              onInputChange={handleClearSupplies}
            />
          )}

          <Autocomplete
            name="poco_codigo"
            label="poco_desc_abrev"
            title="Posto"
            url="postoscomboios"
            unique="poco_codigo"
            onInputChange={handleClearSupplies}
          />

          <Button
            type="button"
            startIcon={<Send />}
            color="default"
            variant="outlined"
            onClick={handleGetNF}
          >
            Buscar Notas Fiscais
          </Button>

          <Table
            radio
            noButton
            cells={header}
            data={nfs}
            loading={loading}
            onCallback={(value) => setItems(value)}
            size="small"
            title="Notas Fiscais"
          />

          <RadioGroup
            row
            name="tipo"
            label="Formato"
            options={[
              { label: 'PDF', value: 'pdf' },
              { label: 'Excel', value: 'xls' },
            ]}
            defaultValue="pdf"
            serialize={(value) => value}
          />
        </S.Wrapper>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
        >
          Exibir
        </Button>

        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(false)}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
